import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/form/button';
import { Typography } from '../../components/typography/typography';
import Icon from '../../components/icons/icon';
import classNames from 'classnames';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { ProductType } from '../../clients/quipli-service/product/product';
import { Location } from '../../clients/quipli-service/location/location';
import { OrderSection } from '../order/order-section/order-section';
import { Collapsible, CollapsibleRef } from '../../components/collapsible/collapsible';
import { OrderType } from '../../clients/quipli-service/order/order';
import { Field, FieldLabel } from '../../components/field/field';
import { QuantityInput } from '../../customer/product-form/quantity-input/quantity-input';
import { UpsertPurchaseOrderProductRequest } from '../../clients/quipli-service/purchase-order/purchase-order';
import { v4 as uuid } from 'uuid';

export const PurchaseLineItemsForm = ({
  location,
  openOnStart = true,
  setItems,
}: {
  location?: Location;
  openOnStart?: boolean;
  setItems?: (orderInputs: UpsertPurchaseOrderProductRequest[]) => void;
}) => {
  const { getMultipleConfigurations } = useAPI();
  // States
  const [type, setType] = useState<ProductType>(ProductType.Product);
  const [customProductEnabled, setCustomProductEnabled] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [extraField, setExtraField] = useState<string>('');
  const [price, setPrice] = useState<number>();
  const [qty, setQty] = useState<number>(1);

  const isDisabled = !name || !price || !qty || (type === ProductType.Part && !extraField);

  // Vars
  const collapsibleRef = useRef<CollapsibleRef>(null);

  // Callbacks
  const collapse = () => {
    collapsibleRef.current?.toggle(false);
  };

  const clear = () => {
    setName('');
    setExtraField('');
    setPrice(undefined);
    setQty(1);
  };

  const submit = () => {
    if (type === ProductType.Service) return;
    if (isDisabled) return;

    const commonFields = {
      id: uuid(),
      name,
      quantity: qty,
      unitPrice: price,
      productType: OrderType.Sale,
    };

    if (type === ProductType.Part) {
      setItems && setItems([{ type, description: extraField, ...commonFields }]);
    } else if (type === ProductType.Custom) {
      setItems && setItems([{ type, description: extraField, ...commonFields }]);
    } else {
      setItems && setItems([{ type, ...commonFields }]);
    }

    clear();
  };

  useEffect(() => {
    if (location === undefined) return;

    getMultipleConfigurations(['CONF_ENABLE_CUSTOM_PRODUCTS']).then((configs) => {
      setCustomProductEnabled(configs.find((c) => c.variable === 'CONF_ENABLE_CUSTOM_PRODUCTS')?.value === '1');
    });
  }, [location]);

  useEffect(() => {
    openOnStart && collapsibleRef.current?.toggle(true);
  }, []);

  return (
    <div className="order-add-product">
      <OrderSection>
        <Collapsible
          ref={collapsibleRef}
          trigger={(isOpen) => (
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Add products to order
              </Typography>
              <Icon icon={isOpen ? 'Close' : 'Plus'} stroke="var(--quipli-black-800)" />
            </div>
          )}
        >
          <div className="flex flex-col gap-3">
            <div className="list-tabs">
              <div className="tabs-wrapper">
                <div
                  className={classNames('tab-item', { active: type === ProductType.Product })}
                  onClick={() => setType(ProductType.Product)}
                  data-cy="product_tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === ProductType.Product ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Product
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: type === ProductType.Part })}
                  onClick={() => {
                    setExtraField('');
                    setType(ProductType.Part);
                  }}
                  data-cy="part-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === ProductType.Part ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Part
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                {customProductEnabled && (
                  <div
                    className={classNames('tab-item', { active: type === ProductType.Custom })}
                    onClick={() => {
                      setExtraField('');
                      setType(ProductType.Custom);
                    }}
                    data-cy="custom-tab"
                  >
                    <Typography
                      type="text-sm"
                      weight="medium"
                      className={type === ProductType.Custom ? 'text-quipli-black-800' : 'text-grey-500'}
                    >
                      Custom
                    </Typography>
                    <div className="divider-active"></div>
                  </div>
                )}
              </div>
            </div>

            <Field label="Name" isRequired type="text" value={name} onChange={(v) => setName(v)} dataCy="field_name" />

            {type === ProductType.Part && <PartForm partNumber={extraField} setPartNumber={setExtraField} />}
            {type === ProductType.Custom && <CustomForm description={extraField} setDescription={setExtraField} />}

            <div className="flex flex-col md:flex-row gap-[12px] justify-between mb-3">
              <Field
                label="Price"
                isRequired
                type="currency"
                value={price}
                onChange={(v) => {
                  if (Number.isNaN(v) && !price) {
                    setPrice(NaN);
                  } else setPrice(v);
                }}
                maximumFractionDigits={2}
                dataCy="field_price"
              />

              <div className="">
                <FieldLabel label="Quantity" />
                <QuantityInput name="Quantity" value={`${qty}`} onChange={(v) => setQty(parseInt(v ?? '1'))} />
              </div>
            </div>

            <div className="button-group">
              <Button
                className="button-secondary"
                onClick={() => {
                  collapse();
                  clear();
                }}
                data-cy="cancel_add_product_to_order"
              >
                Cancel
              </Button>
              <Button className="button-primary" onClick={submit} disabled={isDisabled} data-cy="add_product_to_order">
                Add to order
              </Button>
            </div>
          </div>
        </Collapsible>
      </OrderSection>
    </div>
  );
};

const PartForm = ({ partNumber, setPartNumber }: { partNumber: string; setPartNumber: (n: string) => void }) => {
  return (
    <>
      <Field
        isRequired
        label="Part Number"
        type="text"
        value={partNumber}
        onChange={(v) => setPartNumber(v)}
        dataCy="partf_field_number"
      />
    </>
  );
};

const CustomForm = ({ description, setDescription }: { description: string; setDescription: (d: string) => void }) => {
  return (
    <>
      <Field
        label="Description"
        type="text"
        value={description}
        onChange={(v) => setDescription(v)}
        dataCy="custom_field_description"
      />
    </>
  );
};
